import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import 'ag-grid-enterprise'

import { BooleanFormatter, BooleanRenderer } from '@hr/common/gridcomponents.jsx'
import { deleteCourse } from '../../../actions/competences/courses/coursedelete'
import { getCourse } from '../../../actions/competences/courses/courseget'
import { postCourse } from '../../../actions/competences/courses/coursepost'
import { getAllCoursesForPerson } from '../../../actions/competences/courses/coursesgetallforperson'
//import { getAllCoursesForPersonModified } from '../../../actions/competences/courses/coursesgetallforpersonmodified'
import { getAllCoursesForPersons } from '../../../actions/competences/courses/coursesgetallforpersons'
//import { getAllCoursesModified } from '../../../actions/competences/courses/coursesgetallmodified'
import { addGridData, getLocaleText, removeGridData, resetSorting, updateGridData } from '../../../actions/datagrid'
import { Button } from '../../../common/Button.tsx'
import {
	contextMenu,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../../../common/grid'
import {
	filesIconString,
	IconAddNew,
	pencilIconString,
	restoreIconString,
	TemplateProfileImage,
	trashIconString,
} from '../../../common/icons'
import { PdfReport } from '../../../common/pdfreport'
import { fetchSettings } from '../../../common/settings.js'
import { dateFormatter, dateGetter, numberFormatterFraction, tagFormatter /*useInterval*/ } from '../../../common/utils'
import {
	actionTypeSelector,
	authSelector,
	courseSelector,
	/*coursesModifiedSelector,*/
	coursesSelector,
} from '../../../reducers'
import { AddCourse } from './coursemodal'

const viewStateKey = 'courseViewState'

export default function CourseView({ person }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	const actionType = useSelector(actionTypeSelector)
	const { admin, companyName } = useSelector(authSelector)
	const courses = useSelector(coursesSelector)
	const course = useSelector(courseSelector)

	const [settings, setSettings] = useState({})

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const [modalShow, setModalShow] = useState(false)
	const [showLoading, setShowLoading] = useState(false)

	const gridRef = useRef()

	useEffect(() => {
		if (!person && !isPersonal) {
			dispatch(getAllCoursesForPersons(true))
		}
		return () => dispatch({ type: 'COURSES_UNMOUNT', payload: { courses: null } })
	}, [person, isPersonal, dispatch])

	useEffect(() => {
		if (person) {
			if (person?.personId) {
				dispatch(getAllCoursesForPerson(person?.personId))
			}
		}
	}, [person, dispatch])

	useEffect(() => {
		;(async () => {
			setSettings(await fetchSettings())
		})()
	}, [])
	/*
	const fetchModifiedCourses = useCallback((modifiedSince) => dispatch(getAllCoursesModified(modifiedSince)))
	const fetchModifiedCoursesForPerson = useCallback((personId, modifiedSince) =>
		dispatch(getAllCoursesForPersonModified(person?.personId, modifiedSince)),
	)
	const [modifiedSince, setModifiedSince] = useState(Date.now())
	// Poll modified courses every 30 seconds
	if (import.meta.env.TEST) {
		// production
		useInterval(() => {
			const tmpModifiedSince = modifiedSince
			setModifiedSince(Date.now())
			if (person?.personId) {
				fetchModifiedCoursesForPerson(person?.personId, tmpModifiedSince)
			} else {
				fetchModifiedCourses(tmpModifiedSince)
			}
		}, 30 * 1000)
	}
	const coursesModified = useSelector(coursesModifiedSelector)
	useEffect(() => {
		if (gridRef.current.api) {
			if (coursesModified?.newCourses?.length > 0) {
				addGridData(gridRef, coursesModified.newCourses, 0)
			}
			if (coursesModified?.modifiedCourses?.length > 0) {
				updateGridData(gridRef, coursesModified.modifiedCourses)
			}
			if (coursesModified?.deletedCourses?.length > 0) {
				removeGridData(gridRef, coursesModified.deletedCourses)
			}
		}
	}, [coursesModified])
*/

	useEffect(() => {
		if (actionType.endsWith('_LOADING')) {
			setShowLoading(true)
		} else if (actionType.endsWith('_SUCCEEDED') || actionType.endsWith('_FAIL') || actionType.endsWith('_DONE')) {
			setShowLoading(false)
		}
		if (actionType === 'POST_COURSE_SUCCEEDED') {
			resetSorting(gridRef)
			addGridData(gridRef, [course], 0)
		}
		if (actionType === 'POST_COURSE_EDIT_SUCCEEDED') {
			updateGridData(gridRef, [course])
		}
		if (actionType === 'UPLOAD_DONE') {
			person?.personId ? dispatch(getAllCoursesForPerson(person?.personId)) : dispatch(getAllCoursesForPersons(true))
			setModalShow(false)
		}
		if (actionType === 'GET_COURSE_SUCCEEDED') {
			setModalShow(true)
		}
		if (actionType === 'DELETE_COURSE_SUCCEEDED') {
			removeGridData(gridRef, [course])
		}
	}, [actionType, course, person, dispatch])

	const saveData = (data) => dispatch(postCourse(data))
	const removeCourse = useCallback(
		({ id, data: { personName } }) => {
			if (confirm(`${t('DeleteCourseConfirm', 'Haluatko varmasti poistaa muun koulutuksen?')} (${personName})`)) {
				dispatch(deleteCourse(id))
			}
		},
		[dispatch, t],
	)

	const gridComponents = {
		templateProfileImage: TemplateProfileImage,
	}

	const columnDefs = useMemo(
		() => [
			{
				headerName: t('Number', 'Numero'),
				field: 'personNumber',
				filter: 'agNumberColumnFilter',
				suppressSizeToFit: true,
				minWidth: 115,
				maxWidth: 115,
				width: 115,
			},
			{
				headerName: t('Name', 'Nimi'),
				field: 'personName',
				width: 150,
				tooltipField: 'personName',
			},
			{
				headerName: t('DurationHours', 'Kesto (tuntia)'),
				field: 'durationHours',
				filter: 'agNumberColumnFilter',
				valueFormatter: numberFormatterFraction,
				width: 150,
			},
			{
				headerName: t('DurationDays', 'Kesto (päivää)'),
				field: 'durationDays',
				filter: 'agNumberColumnFilter',
				width: 150,
			},
			{
				headerName: t('DurationMonths', 'Kesto (kuukautta)'),
				field: 'durationMonths',
				filter: 'agNumberColumnFilter',
				width: 150,
			},
			{
				headerName: t('DurationYears', 'Kesto (vuotta)'),
				field: 'durationYears',
				filter: 'agNumberColumnFilter',
				width: 150,
			},
			{
				headerName: t('Course', 'Kurssi'),
				field: 'courseCodeItem.codeValue',
				tooltipField: 'courseCodeItem.codeValue',
			},
			{
				headerName: t('ArrangedBy', 'Järjestävä organisaatio'),
				field: 'trainerOrganizer',
			},
			{
				headerName: t('Start', 'Aloitus'),
				cellClass: 'dateType',
				field: 'startDateTime',
				filter: 'agDateColumnFilter',
				width: 175,
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('End', 'Lopetus'),
				cellClass: 'dateType',
				field: 'endDateTime',
				filter: 'agDateColumnFilter',
				width: 175,
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('ExpirationDate', 'Voimassaolon päät.pvm'),
				cellClass: 'dateType',
				field: 'certificationValidUntil',
				filter: 'agDateColumnFilter',
				width: 175,
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('Description', 'Selite'),
				field: 'educationContent',
				tooltipField: 'educationContent',
			},
			{
				headerName: t('LicenseCertificateCard', 'Lisenssi/sertifikaatio/kortti'),
				field: 'certificationName',
				tooltipField: 'certificationName',
			},
			{
				headerName: t('Trainer', 'Kouluttaja'),
				field: 'trainerName',
				tooltipField: 'trainerName',
			},
			{
				headerName: t('Internal', 'Sisäinen'),
				field: 'trainerType',
				valueGetter: (params) => params.data?.trainerType === 'INTERNAL',
				cellRenderer: BooleanRenderer,
				filter: 'agSetColumnFilter',
				filterParams: {
					valueFormatter: BooleanFormatter,
				},
			},
			{
				field: 'supervisor',
				headerName: t('Supervisor', 'Esihenkilö'),
			},
			{
				field: 'supervisor2',
				headerName: `${t('Supervisor', 'Esihenkilö')} 2`,
			},
			{
				field: 'dim1',
				headerName: settings.dim1name || t('Dimension1', 'Kustannuspaikka'), // costUnitName
			},
			{
				field: 'dim2',
				headerName: settings.dim2name || t('Dimension2', 'Projekti'), // projectName
			},
			{
				field: 'dim3',
				headerName: settings.dim3name || t('Dimension3', 'Dimensio 3'),
			},
			{
				field: 'dim4',
				headerName: settings.dim4name || t('Dimension4', 'Dimensio 4'),
			},
			{
				field: 'dim5',
				headerName: settings.dim5name || t('Dimension5', 'Dimensio 5'),
			},
			{
				field: 'dim6',
				headerName: t('BusinessId', 'Y-tunnus'),
			},
			{
				headerName: t('Keywords', 'Avainsanat'),
				field: 'tags',
				valueGetter: (params) => tagFormatter(params.data?.tags),
				tooltipValueGetter: (params) => tagFormatter(params.data?.tags),
			},
		],
		[settings, t],
	)

	const initialState = useMemo(
		() => ({
			columnVisibility: {
				...(isPersonal || person
					? { hiddenColIds: ['personNumber', 'personName', 'educationContent', 'trainerType', 'tags'] }
					: { hiddenColIds: ['supervisor', 'supervisor2'] }),
			},
		}),
		[isPersonal, person],
	)

	const getContextMenuItems = useCallback(
		(params) => {
			let items = []

			if (!isPersonal) {
				items.push({
					name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
					action: () => {
						restoreViewState(viewStateKey, initialState)
					},
					icon: restoreIconString,
				})

				items.push('separator')
			}

			if (!params.api.isPivotMode()) {
				items.push(
					{
						name: t('Edit', 'Muokkaa'),
						icon: pencilIconString,
						action: () => dispatch(getCourse(params.node.id)),
					},
					{
						name: t('Copy', 'Kopioi'),
						icon: filesIconString,
						action: () => dispatch(getCourse(params.node.id, true)), // delete courseId, personId, attachments from data
					},
				)

				if (admin) {
					items.push({
						name: t('Delete', 'Poista'),
						icon: trashIconString,
						action: () => removeCourse(params.node),
					})
				}
				items.push('separator')
			}
			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('OtherEducation', 'Muu koulutus'),
					companyname: companyName,
				}),
			)
			return items
		},
		[companyName, dispatch, initialState, isPersonal, removeCourse, t, admin],
	)

	const doubleClickAction = (row) => !Number.isNaN(Number(row.node.id)) && dispatch(getCourse(row.node.id))

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}

			{modalShow && <AddCourse show setShow={setModalShow} courseData={course} saveData={saveData} />}
			{!location.pathname.startsWith('/mgmt/persons') && (
				<Row>
					<Col sm="4"></Col>
					<Col sm="8" align="right">
						<Row className="g-2">
							<Col />
							<Col md="auto">
								<Button
									testId="add-competence"
									className="competencebtn addbutton"
									onClick={() => {
										// empty fields when creating new
										dispatch({
											type: 'COURSE_UNMOUNT',
											payload: {
												course: {
													courseId: 0,
													personId: person?.personId,
													personNumber: person?.nr,
													personName: person?.name,
												},
											},
										})
										setModalShow(true)
									}}
								>
									<IconAddNew /> {t('AddCourse', 'Lisää koulutus')}
								</Button>
							</Col>
						</Row>
					</Col>
				</Row>
			)}

			<Row className="ag-theme-material styles-ag flex-grow-1">
				<AgGridReact
					{...gridSettings}
					loading={showLoading}
					ref={gridRef}
					components={gridComponents}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					sideBar={sideBar}
					rowData={courses}
					getContextMenuItems={getContextMenuItems}
					statusBar={statusBar}
					onRowDoubleClicked={doubleClickAction}
					getRowId={(params) => String(params.data.courseId)}
					getLocaleText={(params) => getLocaleText(t, params)}
					animateRows
					suppressCellFocus
					enableCellTextSelection
					initialState={isPersonal || person ? initialState : loadViewState(viewStateKey)}
					onStateUpdated={({ state }) => !isPersonal && saveViewState(state, viewStateKey)}
					debug={import.meta.env.DEV}
				/>
			</Row>
		</>
	)
}
